import { Flappy, type FlappyDuelView } from '@minigame-race/contracts';
import { useEffect, useRef, useState } from 'react';
import { color } from '../theme.js';

/**
 * Flappy duel board.
 *
 * Flow, from the ground up:
 *  1. COUNTDOWN — a synced 3-2-1 (from the engine's `goAtEpoch`). Both birds hover
 *     dead-centre; the world does not scroll and nothing can crash yet.
 *  2. GO — both players begin flying the SAME seeded course at the same instant.
 *     Flight time is measured from a LOCAL timestamp taken at GO (never from the
 *     server epoch, which would jump by clock skew), so the world starts at x=0.
 *  3. FLY — tap/space to flap; gravity pulls you down; thread the pipe gaps.
 *  4. CRASH — the bird STOPS at the wall it hit (its x/y freeze, the world stops
 *     scrolling — it does not slide through). The engine ends the duel: the first
 *     to crash loses; same pillar within the tie window = draw.
 *
 * The opponent is drawn as a translucent ghost slightly BEHIND your bird, its
 * altitude smoothed toward the server's last-known value.
 */
export function FlappyBoard({
  view,
  onMove,
}: {
  view: FlappyDuelView & { yourSeat: 0 | 1 };
  onMove: (move: unknown) => void;
}) {
  const { courseSeed, goAtEpoch, over, winnerSeat, yourSeat } = view;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Live view values read inside the rAF loop via refs (props change each tick).
  const goAtRef = useRef<number | null>(null);
  goAtRef.current = goAtEpoch;
  const oppRef = useRef<{ y: number | null; alive: boolean }>({ y: null, alive: true });
  oppRef.current = { y: view.opponentY, alive: view.opponentAlive };
  const overRef = useRef(over);
  overRef.current = over;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  const [countLabel, setCountLabel] = useState<string>('3');
  const [flying, setFlying] = useState(false);
  const [crashedUi, setCrashedUi] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !courseSeed) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pipes = Flappy.buildCourse(courseSeed, Flappy.PIPE_COUNT);
    const W = canvas.width;
    const H = canvas.height;
    const SY = H / Flappy.WORLD_HEIGHT; // vertical scale (canvas px per world unit)

    let y = Flappy.WORLD_HEIGHT / 2;
    let vy = 0;
    let started = false;
    let crashed = false;
    let goLocalPerf = 0; // performance.now() captured the instant WE detect GO
    let frozenT = 0; // flight time frozen at the crash instant (world stops)
    let last = performance.now();
    let raf = 0;
    let ghostY = Flappy.WORLD_HEIGHT / 2; // smoothed opponent altitude

    // Flight time since GO, in ms — FROZEN once crashed so the world stops
    // scrolling. Guarded against a bogus goLocalPerf (would explode the world
    // scroll and cause an instant phantom crash).
    const flightMs = (): number => {
      if (!started || goLocalPerf === 0) return 0;
      if (crashed) return frozenT;
      return Math.max(0, performance.now() - goLocalPerf);
    };

    const flap = (): void => {
      if (crashed || !started) return;
      vy = Flappy.FLAP_VELOCITY;
      onMoveRef.current({ type: 'flap', tMs: Math.round(flightMs()), y: Math.round(y) });
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.code === 'Space' || e.key === ' ' || e.key === 'ArrowUp') { e.preventDefault(); flap(); }
    };
    const onPointer = (e: Event): void => { e.preventDefault(); flap(); };
    window.addEventListener('keydown', onKey);
    canvas.addEventListener('pointerdown', onPointer);

    const worldXAt = (t: number): number => Flappy.BIRD_X + Flappy.SCROLL_SPEED * (t / 1000);

    // Pipe index hit (-1 = ground/ceiling), or null if the bird is clear.
    const crashPipe = (): number | null => {
      if (y - Flappy.BIRD_RADIUS < 0 || y + Flappy.BIRD_RADIUS > Flappy.WORLD_HEIGHT) return -1;
      const wx = worldXAt(flightMs());
      for (let i = 0; i < pipes.length; i++) {
        const p = pipes[i]!;
        if (Math.abs(p.x - wx) < Flappy.BIRD_RADIUS + Flappy.PIPE_HALF_WIDTH) {
          const top = p.gapCenterY - p.gap / 2;
          const bot = p.gapCenterY + p.gap / 2;
          if (y - Flappy.BIRD_RADIUS < top || y + Flappy.BIRD_RADIUS > bot) return i;
        }
      }
      return null;
    };

    const draw = (): void => {
      const camX = worldXAt(flightMs()) - Flappy.BIRD_X;
      ctx.fillStyle = '#0e1a12';
      ctx.fillRect(0, 0, W, H);
      // ground line
      ctx.fillStyle = 'rgba(166,255,46,.10)';
      ctx.fillRect(0, H - 4, W, 4);
      // pipes
      for (const p of pipes) {
        const sx = p.x - camX;
        if (sx < -50 || sx > W + 50) continue;
        const top = (p.gapCenterY - p.gap / 2) * SY;
        const bot = (p.gapCenterY + p.gap / 2) * SY;
        ctx.fillStyle = '#3fa14a';
        ctx.fillRect(sx - Flappy.PIPE_HALF_WIDTH, 0, Flappy.PIPE_HALF_WIDTH * 2, top);
        ctx.fillRect(sx - Flappy.PIPE_HALF_WIDTH, bot, Flappy.PIPE_HALF_WIDTH * 2, H - bot);
        ctx.fillStyle = 'rgba(198,255,94,.5)';
        ctx.fillRect(sx - Flappy.PIPE_HALF_WIDTH, top - 5, Flappy.PIPE_HALF_WIDTH * 2, 5);
        ctx.fillRect(sx - Flappy.PIPE_HALF_WIDTH, bot, Flappy.PIPE_HALF_WIDTH * 2, 5);
      }
      // opponent ghost — slightly BEHIND your bird, translucent, smoothed altitude
      const opp = oppRef.current;
      if (typeof opp.y === 'number') {
        ctx.globalAlpha = opp.alive ? 0.4 : 0.18;
        drawBird(ctx, Flappy.BIRD_X - 22, ghostY * SY, color.cyan, SY);
        ctx.globalAlpha = 1;
      }
      // you
      drawBird(ctx, Flappy.BIRD_X, y * SY, crashed ? color.redBright : color.limeBright, SY, vy);
    };

    const loop = (): void => {
      const now = performance.now();
      const dt = Math.min(1 / 30, (now - last) / 1000);
      last = now;

      const goAt = goAtRef.current;
      // Detect GO once: begin flight from a LOCAL clock, centred, at rest.
      if (!started && goAt !== null && Date.now() >= goAt) {
        started = true;
        goLocalPerf = performance.now();
        y = Flappy.WORLD_HEIGHT / 2;
        // A gentle upward nudge at GO (like real Flappy) so the bird doesn't sink
        // to the ground before the player has oriented and tapped.
        vy = Flappy.FLAP_VELOCITY * 0.6;
        setFlying(true);
        setCountLabel('');
      }
      // countdown label (only before GO)
      if (!started && goAt !== null) {
        const remain = Math.max(0, goAt - Date.now());
        setCountLabel(remain > 250 ? String(Math.ceil(remain / 1000)) : 'GO!');
      }

      if (started && !crashed) {
        vy += Flappy.GRAVITY * dt;
        if (vy > Flappy.MAX_FALL_SPEED) vy = Flappy.MAX_FALL_SPEED;
        y += vy * dt;
        const hit = crashPipe();
        if (hit !== null) {
          // Freeze exactly at the wall: clamp y to the boundary and stop the world.
          crashed = true;
          frozenT = performance.now() - goLocalPerf;
          if (y < Flappy.BIRD_RADIUS) y = Flappy.BIRD_RADIUS;
          if (y > Flappy.WORLD_HEIGHT - Flappy.BIRD_RADIUS) y = Flappy.WORLD_HEIGHT - Flappy.BIRD_RADIUS;
          setCrashedUi(true);
          onMoveRef.current({ type: 'crash', tMs: Math.round(frozenT), y: Math.round(y), pipeIndex: hit });
        }
      } else if (!started) {
        y = Flappy.WORLD_HEIGHT / 2 + Math.sin(now / 300) * 6; // gentle pre-GO hover
      }

      // Smooth the ghost toward the last server-known altitude.
      const oy = oppRef.current.y;
      if (typeof oy === 'number') ghostY += (oy - ghostY) * Math.min(1, dt * 12);

      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      canvas.removeEventListener('pointerdown', onPointer);
    };
    // Re-init only on a new course (new segment).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSeed]);

  const status = over
    ? winnerSeat === yourSeat
      ? '🏆 You survived — you win!'
      : winnerSeat === null
        ? '🤝 Both crashed together — draw!'
        : '💥 You crashed first — you lose.'
    : crashedUi
      ? 'Crashed — waiting on your rival…'
      : flying
        ? 'Tap to fly!'
        : 'Get ready…';

  return (
    <div style={{ width: 'min(92cqw, 320px)', height: '100cqh', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(1.4cqh,8px)', minHeight: 0 }}>
      <div style={{ textAlign: 'center', fontSize: 'clamp(11px,2.4cqmin,15px)', fontWeight: 700, flexShrink: 0, color: over ? (winnerSeat === yourSeat ? color.lime : winnerSeat === null ? color.amber : color.redBright) : crashedUi ? color.redBright : color.lime, minHeight: 18 }}>
        {status}
      </div>
      {/* canvas fills the remaining height; its width follows the 300:470 aspect
          and is centred — so the tall board never overflows the race slot. */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center' }}>
        <canvas
          ref={canvasRef}
          width={300}
          height={470}
          style={{
            height: '100%',
            maxWidth: '100%',
            aspectRatio: '300 / 470',
            borderRadius: 12,
            border: '1px solid rgba(166,255,46,.2)',
            background: '#0e1a12',
            touchAction: 'none',
            cursor: 'pointer',
            display: 'block',
          }}
        />
        {!flying && countLabel && (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: 'clamp(44px,20cqmin,88px)', fontWeight: 900, color: countLabel === 'GO!' ? color.limeBright : '#fff', textShadow: '0 3px 16px rgba(0,0,0,.8)', animation: 'nkPop .3s ease-out' }}>
              {countLabel}
            </span>
          </div>
        )}
      </div>
      <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.7cqmin,12px)', flexShrink: 0, color: 'rgba(234,255,234,.4)', lineHeight: 1.4 }}>
        Tap / SPACE to fly. The cyan ghost is your rival.<br />First to hit a pipe loses.
      </div>
    </div>
  );
}

function drawBird(ctx: CanvasRenderingContext2D, x: number, y: number, fill: string, sy: number, vy = 0): void {
  const r = Flappy.BIRD_RADIUS * sy;
  const tilt = Math.max(-0.5, Math.min(0.9, vy / 600));
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(tilt);
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffb020';
  ctx.beginPath();
  ctx.moveTo(r - 2, -3);
  ctx.lineTo(r + 6, 0);
  ctx.lineTo(r - 2, 3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#0c1408';
  ctx.beginPath();
  ctx.arc(r * 0.3, -r * 0.3, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
